import { useToast } from '@hover/blueprint';
import type { GraphQLError } from 'graphql';
import { useCallback } from 'react';

import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

interface ShowErrorProps {
  error: Error | GraphQLError | undefined;
  formatError?: (errorMessage: string) => string;
  isDetailed?: boolean;
}

interface ShowGraphQlErrorsProps extends Omit<ShowErrorProps, 'error'> {
  errors: readonly GraphQLError[] | undefined;
}

/**
 * A hook for displaying error messages.
 *
 * @return {object} { showError, showGraphQlErrors }
 */
export const useErrors = () => {
  const toast = useToast({ props: { bgColor: 'brandRed.200' } });
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  /**
   * Displays an error message in system toast.
   *
   * @param {Error | GraphQLError | undefined} error - the error object to display
   * @param {function} formatError - optional function for formatting the error messages
   * @param {boolean} isDetailed - optional flag for displaying detailed error messages
   * @return {void}
   */
  const showError = useCallback(
    ({ error, formatError, isDetailed }: ShowErrorProps) => {
      const errorMessage = isDetailed
        ? `- ${error?.message || t('error.unknownError')}`
        : '';
      const message = formatError ? formatError(errorMessage) : errorMessage;

      toast({ props: { message } });
    },
    // toast can't be a dependency because it's a new object on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  /**
   * Shows GraphQL errors in separate toasts.
   *
   * @param {readonly GraphQLError[] | undefined} errors - the array of GraphQL errors
   * @param {function} formatError - optional function for formatting the error messages
   * @param {boolean} isDetailed - optional flag for displaying detailed error messages
   * @return {void}
   */
  const showGraphQlErrors = useCallback(
    ({ errors, formatError, isDetailed }: ShowGraphQlErrorsProps) => {
      if (errors?.length) {
        errors.forEach(error => showError({ error, formatError, isDetailed }));
      }
    },
    [showError],
  );

  return {
    showError,
    showGraphQlErrors,
  };
};
